import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { useSearchParams } from "react-router-dom";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { ITableColumn } from "interfaces/Common";
import TableSortLabel from "components/TableSortLabel/TableSortLabel";
import TableInputFilter from "components/TableInputFilter/TableInputFilter";
import TableDatepickerFilter from "components/TableDatepickerFilter/TableDatepickerFilter";
import useSnackbar from "hooks/useSnackbar";
import useFetchTableData from "hooks/useFetchTableData";
import Loading from "components/Loading/Loading";
import {
  createUrlParams,
  formatDashDate,
} from "helpers/CommonHelper";
import NoTableItems from "components/Tables/NoTableItems/NoTableItems";
import { IBillingInClinic, IBillingLabFilters } from "interfaces/Billing";
import BillingService from "services/BillingService";
import tableStyles from "styles/shared/table.module.scss";
import styles from "./BillingLabTable.module.scss";
import TableRangeDatepickerFilter from "components/TableRangeDatepickerFilter/TableRangeDatepickerFilter";
import EllipsisTableCell from "components/EllipsisTableCell/EllipsisTableCell";

interface BillingLabTableProps {
  filters: IBillingLabFilters;
  setFilters: Dispatch<SetStateAction<IBillingLabFilters>>;
}

const BillingLabTable = (props: BillingLabTableProps) => {
  const { filters, setFilters } = props;

  const [, setSearchParams] = useSearchParams();

  const onPageIncrement = useCallback(
    () => setFilters((prev) => ({ ...prev, page: prev.page + 1 })),
    [setFilters]
  );

  const { snackbarComponent, setSnackbarError } =
    useSnackbar();
  const { data, loading, scrollableParentRef, lastElementRef } =
    useFetchTableData<IBillingInClinic, IBillingLabFilters>({
      filters,
      onPageIncrement,
      fetchAPI: BillingService.getLab,
      onError: setSnackbarError,
    });

  // Set default date filter to today if not already set
  // useEffect(() => {
  //   if (!filters.dates) {
  //     const today = moment().format("YYYY-MM-DD"); // Format today's date using moment
  //     const date90DaysAgo = moment().subtract(90, 'days').format('YYYY-MM-DD');

  //     setFilters((prev) => ({
  //       ...prev,
  //       dates: date90DaysAgo + "~`" + today,
  //     }));
  //   }
  // }, [filters.dates, setFilters]);

  // fewer columns are shown on single order billing history
  const tableColumns: ITableColumn[] = [
    { label: "Account", sortProperty: "client" },
    { label: "Patient", sortProperty: "patient" },
    { label: "DOB", sortProperty: "dob" },
    { label: "Date Range", sortProperty: "dates" },
    { label: "15 Min Intervals", sortProperty: "enc_count" },
    { label: "Test Count", sortProperty: "test_count" },
    { label: "Gait", sortProperty: "gait" },
    { label: "Balance", sortProperty: "balance" },
    { label: "Angles", sortProperty: "angles" },
    { label: "Symmetry", sortProperty: "symmetry" }
  ];

  useEffect(() => {
    const {
      page,
      page_size,
      _total_count,
      // push only props that can be filtered
      ...urlParams
    } = filters;

    setSearchParams(createUrlParams(urlParams), { replace: true });
  }, [filters, setSearchParams]);

  return (
    <>
      <div
        className={`h-full relative -mr-5 ${styles.billing_table_wrapper} ${styles.limit_cell_widths} `}
      >
        <TableContainer
          className={tableStyles.shared_table_container}
          ref={scrollableParentRef}
        >
          <Table>
            <TableHead>
              <TableRow>
                {tableColumns.map(({ label, sortProperty }, index) => (
                  <TableCell key={index}>
                    {sortProperty ? (
                      <TableSortLabel
                        whiteTheme
                        label={label}
                        sortProperty={sortProperty}
                        orderBy={filters._order_by}
                        onSort={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            page: 1,
                            _order_by: value,
                          }))
                        }
                      />
                    ) : (
                      label
                    )}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell />
                <TableCell>
                  <TableInputFilter
                    placeholder="Search by patient name"
                    value={filters.patient}
                    onChange={(patient) =>
                      setFilters((prev) => ({ ...prev, page: 1, patient }))
                    }
                  />
                </TableCell>
                <TableCell>
                  <TableDatepickerFilter
                    removeNextDay
                    value={filters.dob}
                    onChange={(dob) =>
                      setFilters((prev) => ({ ...prev, page: 1, dob }))
                    }
                  />
                </TableCell>
                <TableCell>
                  <TableRangeDatepickerFilter
                    value={filters.dates}
                    onChange={(dates) =>
                      setFilters((prev) => ({ ...prev, page: 1, dates }))
                    }
                  />
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((billing, index) => (
                <TableRow
                  key={billing.id}
                  ref={index === data.length - 1 ? lastElementRef : null}
                >
                  <EllipsisTableCell text={billing.client} />
                  <EllipsisTableCell
                    text={billing.patient}
                  />
                  <TableCell>{formatDashDate(billing.dob)}</TableCell>
                  <TableCell>{formatDashDate(billing.date)}</TableCell>
                  <TableCell>{billing.encCount ?? '-'}</TableCell>
                  <TableCell>
                    {
                      (billing.gait ?? 0) +
                      (billing.balance ?? 0) +
                      (billing.angles ?? 0) +
                      (billing.symmetry ?? 0) 
                    }
                  </TableCell>
                  <TableCell>{billing.gait ?? 0}</TableCell>
                  <TableCell>{billing.balance ?? 0}</TableCell>
                  <TableCell>{billing.angles ?? 0}</TableCell>
                  <TableCell>{billing.symmetry ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && !data.length && (
          <div className="mt-7">
            <NoTableItems icon="icon_search" message="No results found" />
          </div>
        )}

        <Loading loading={loading} />

      </div>

      {snackbarComponent}
    </>
  );
};

export default BillingLabTable;
